// Azimuth / elevation geometry shared by channel derivation (channels.ts) and
// the LAY-02 / LAY-03 rules. See README.md "Forward direction".
//
// Two, DELIBERATELY SEPARATE, conventions for "forward":
//
//  - `facingYawRad` set: the backend's convention (`admin_optimize.py`
//    `_layout_positions`): a position at azimuth `a` from yaw `y` sits at
//    listener + d*(sin(y+a), cos(y+a)) in (x, z). We invert that exactly:
//    given an offset (dx, dz), az = atan2(dx, dz) - yaw.
//  - `facingYawRad` null: `generateSpeakerLayout` (room-flow) assumes the
//    front wall is at z = 0 and forward is -Z, placing az at
//    (d*sin(az), -d*cos(az)). Inverted: az = atan2(dx, -dz).
//
// These two formulas are NOT the same function evaluated at yaw = π — they
// are independent conventions per the README ("When null... as
// generateSpeakerLayout assumes"), and re-deriving one from the other by
// substituting yaw = π flips the sign of az. Implementing them as two
// literal, separately-tested branches (each the exact inverse of the
// formula that PLACED the speakers) is the conservative choice: it
// guarantees any layout `generateSpeakerLayout` produces decodes back to
// precisely the azimuth it was built at (the README's golden requirement),
// and it guarantees a position built from the backend's own formula (tested
// directly in geometry.test.ts) decodes back too — without relying on the
// two conventions agreeing with each other in some other case that no test
// exercises.

export interface Vec2 { x: number; z: number }

/** Unit forward vector in the (x, z) plane per the doc's facing convention. */
export function forwardVector(doc: { facingYawRad: number | null }): Vec2 {
  if (doc.facingYawRad != null) {
    return { x: Math.sin(doc.facingYawRad), z: Math.cos(doc.facingYawRad) };
  }
  return { x: 0, z: -1 };
}

function normalizeDeg(deg: number): number {
  let d = deg % 360;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}

/** Signed azimuth in degrees from the listener toward a world-space offset
 *  (dx, dz) = targetPos - listenerPos, in (-180, 180], positive to the
 *  right (whichever physical direction that is per the active convention —
 *  see module doc). This is the exact inverse of the formula that places
 *  channels at a given azimuth, so it round-trips generated layouts. */
export function azimuthDeg(doc: { facingYawRad: number | null }, dx: number, dz: number): number {
  const rad = doc.facingYawRad != null
    ? Math.atan2(dx, dz) - doc.facingYawRad
    : Math.atan2(dx, -dz);
  return normalizeDeg((rad * 180) / Math.PI);
}

/** Smallest angular difference between two azimuths in degrees, [0, 180]. */
export function angularDiffDeg(a: number, b: number): number {
  return Math.abs(normalizeDeg(a - b));
}

/** True when offset (dx, dz) lies on the same side as forward (in front of
 *  the listener). Exactly on the plane through the listener perpendicular
 *  to forward (dot === 0) counts as front — see LAY-03 rule for the
 *  ambiguous-direction fallback this borders on. */
export function isInFront(doc: { facingYawRad: number | null }, dx: number, dz: number): boolean {
  const f = forwardVector(doc);
  return dx * f.x + dz * f.z >= 0;
}

/** First positive-`t` intersection of ray `origin + t*dir` (t >= 0) with a
 *  simple polygon's boundary edges, in the (x, z) plane. Returns `Infinity`
 *  if the ray never exits (degenerate input). Works for non-convex (e.g.
 *  L-shaped) polygons since it just takes the nearest edge hit — correct
 *  for a ray cast from a point inside the room. */
export function raycastPolygon(
  origin: Vec2, dir: Vec2, polygon: readonly { x: number; y: number }[],
): number {
  const EPS = 1e-9;
  let best = Infinity;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % n];
    const ex = b.x - a.x;
    const ez = b.y - a.y;
    const denom = dir.x * ez - dir.z * ex;
    if (Math.abs(denom) < EPS) continue; // parallel
    const qpx = a.x - origin.x;
    const qpz = a.y - origin.z;
    const t = (qpx * ez - qpz * ex) / denom;
    const u = (qpx * dir.z - qpz * dir.x) / denom;
    if (t > EPS && u >= -EPS && u <= 1 + EPS && t < best) best = t;
  }
  return best;
}

/** Elevation arc in degrees per LAY-03: `e = atan2(y - earY, horizontalDist)`
 *  for a speaker in front of the listener, `180 - e` behind. */
export function elevationArcDeg(
  doc: { facingYawRad: number | null },
  dx: number, dz: number, dy: number,
): number {
  const horiz = Math.hypot(dx, dz);
  const e = (Math.atan2(dy, horiz) * 180) / Math.PI;
  return isInFront(doc, dx, dz) ? e : 180 - e;
}
